// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — CommunicationCards
// Shared corporate communication cards for Level1 and Level2
// Redesign: white cards with top accent, centered icon circle
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { PM } from '../../../../../config/processMapStyles';
import {
  IconCompliance,
  IconGobierno2,
  IconPersonas,
  IconEntorno,
  IconMedioAmbiente,
  IconTech,
} from './ProcessMapIcons';

// ─── Icon map ────────────────────────────────────────────────

type IconFC = React.FC<{ size?: number; color?: string }>;

const ICON_MAP: Record<string, IconFC> = {
  compliance: IconCompliance,
  gobierno: IconGobierno2,
  personas: IconPersonas,
  entorno: IconEntorno,
  medioambiente: IconMedioAmbiente,
  tech: IconTech,
};

// ─── Card data ───────────────────────────────────────────────

interface CommCard {
  titulo: string;
  subtitulo: string;
  icon: string;
}

const COMM_CARDS: CommCard[] = [
  {
    titulo: 'Cumplimiento Normativo',
    subtitulo: 'Auditoría ISO 9001 completada con éxito',
    icon: 'compliance',
  },
  {
    titulo: 'Buen Gobierno',
    subtitulo: 'Nuevo código de conducta aprobado por el consejo',
    icon: 'gobierno',
  },
  {
    titulo: 'Personas',
    subtitulo: 'Plan de desarrollo profesional 2026 en marcha',
    icon: 'personas',
  },
  { titulo: 'Entorno', subtitulo: 'Análisis de riesgos del entorno actualizado', icon: 'entorno' },
  {
    titulo: 'Medio Ambiente',
    subtitulo: 'Certificación ambiental renovada para 2026',
    icon: 'medioambiente',
  },
  { titulo: 'Tecnología', subtitulo: 'Despliegue de V·IANet 2.0 en producción', icon: 'tech' },
];

// ═══════════════════════════════════════════════════════════════
// CommunicationCards — wrapper with section header + 6 cards
// ═══════════════════════════════════════════════════════════════

export function CommunicationCards() {
  return (
    <div
      style={{
        background: PM.surface,
        borderRadius: PM.card.borderRadius,
        border: PM.card.border,
        boxShadow: PM.card.shadow,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* Section header */}
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: PM.textSec,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}
      >
        Comunicación Corporativa
      </div>

      {/* 6 cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {COMM_CARDS.map((card, idx) => (
          <CommCardItem key={idx} card={card} />
        ))}
      </div>
    </div>
  );
}

// ─── Individual card ──────────────────────────────────────────

function CommCardItem({ card }: { card: CommCard }) {
  const [hovered, setHovered] = useState(false);
  const IconComponent = ICON_MAP[card.icon];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: '1 1 0',
        minWidth: 140,
        background: '#FFFFFF',
        borderRadius: 12,
        border: '1px solid #E8EBF0',
        textAlign: 'center',
        paddingBottom: 20,
        boxShadow: hovered ? '0 6px 20px rgba(31,56,100,0.08)' : '0 1px 4px rgba(31,56,100,0.06)',
        cursor: 'pointer',
        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
        transform: hovered ? 'translateY(-2px)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Top accent line */}
      <div
        style={{
          height: 4,
          background: '#1F3864',
          borderRadius: '12px 12px 0 0',
        }}
      />

      {/* Centered icon circle */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#F2F2F2',
          margin: '20px auto 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {IconComponent && <IconComponent size={26} color="#1F3864" />}
      </div>

      {/* Title */}
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: '#1E293B',
          lineHeight: 1.3,
          padding: '0 12px',
          marginBottom: 6,
        }}
      >
        {card.titulo}
      </div>

      {/* Subtitle */}
      <div
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: '#94A3B8',
          lineHeight: 1.4,
          padding: '0 12px',
        }}
      >
        {card.subtitulo}
      </div>
    </div>
  );
}
